import { useEffect, useRef, useState, type FC } from 'react';

import { Carousel } from '@/core/tamagoshi/Carousel/Carousel';
import { useLazy } from '@/domains/core/page/hooks/useLazy';
import type { DetailMdScore } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import type { RhinoProduct } from '@/domains/productDiscovery/ProductPage/interfaces/RhinoProduct';

import { MDRankingPlaylistContent } from './components/PlaylistContent/PlaylistContent';
import { PlaylistSkeleton } from './components/PlaylistSkeleton/PlaylistSkeleton';
import { PlaylistTitle } from './components/PlaylistTitle/PlaylistTitle';
import { useMdsWithRanking } from './hooks/useMDRankingPlaylist';
import { fetchMDRankingRhinoProducts } from './services/rhinoApi';
import { basedOn, customersTopProducts } from './translations';

import styles from './mdRankingPlaylist.module.scss';

export const MDRankingPlaylist: FC<unknown> = () => {
  const [fetching, setFetching] = useState(true);

  const { articleId } = useDetail();

  const mdsWithRanking = useMdsWithRanking();
  const [selectedMd, setSelectedMd] = useState<DetailMdScore>();

  const [products, setProducts] = useState<RhinoProduct[]>([]);

  const title = customersTopProducts.format({
    numOfProducts: products.length || 5,
  }) as string;
  const subtitle = selectedMd?.description
    ? `${basedOn}: ${selectedMd?.description}`
    : '';

  const playlistTitle = (
    <PlaylistTitle
      text={title}
      subtitle={subtitle}
      mds={mdsWithRanking}
      selectedMd={selectedMd}
      setSelectedMd={setSelectedMd}
    />
  );

  const ref = useRef(null);
  const { hasBeenDisplayed } = useLazy(ref, true, {
    rootMargin: '0px',
  });

  useEffect(() => {
    setSelectedMd(mdsWithRanking.length ? mdsWithRanking[0] : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  useEffect(() => {
    if (hasBeenDisplayed && selectedMd) {
      (async () => {
        try {
          setFetching(true);
          const response = await fetchMDRankingRhinoProducts(
            selectedMd.ranking.map((item) => item.articleId),
          );
          setProducts(response);
          setFetching(false);
        } catch (e) {
          setProducts([]);
          setFetching(false);
        }
      })();
    } else {
      setProducts([]);
    }
  }, [selectedMd, hasBeenDisplayed]);

  if (!fetching && mdsWithRanking.length === 0) return null;
  if (fetching || !hasBeenDisplayed)
    return (
      <div
        className={styles.container}
        ref={ref}
        data-testid="MDRankingPlaylistSkeleton"
      >
        <Carousel
          title={playlistTitle}
          controlsClassName={styles.controls}
          sliderClassName={styles.slider}
        >
          <PlaylistSkeleton />
        </Carousel>
      </div>
    );

  return (
    <MDRankingPlaylistContent
      products={products}
      playlistTitle={playlistTitle}
      title={title}
    />
  );
};

MDRankingPlaylist.displayName = 'MDRankingPlaylist';
